import React from 'react'

// WebSocket.readyState values (named here to avoid relying on the global in
// environments where only MozWebSocket exists).
const WS_CONNECTING = 0
const WS_OPEN = 1

/**
 * Resilient WebSocket wrapper.
 *
 * The socket is held as an instance variable (this.ws), NOT in React state:
 * it is not render-relevant (render() is an empty div), and keeping it off
 * state removes the async-setState race where handlers could be attached to a
 * stale socket. All lifecycle transitions are logged via diag() behind the
 * `debug` prop so production logs stay quiet.
 */
export class Websocket extends React.Component {
  constructor(props) {
    super(props)
    this.ws = null
    this.attempts = 1
    this.timeoutID = null
    // Timer that confirms a connection has stayed open long enough to be
    // considered healthy; only then is the backoff counter reset (see onopen).
    this.stableTimerID = null
    this.shouldReconnect = props.reconnect
    this.sendMessage = this.sendMessage.bind(this)
  }

  // How long a socket must stay open before its connection is treated as
  // stable and the exponential backoff is reset. Prevents a connection that
  // opens then immediately closes from pinning reconnects at the attempts===1
  // interval (~1000ms) forever — defense in depth against a flapping socket.
  get stableConnectionThresholdMs() {
    return 5000
  }

  // Lifecycle diagnostics (item 10). Off unless debug is enabled.
  diag(event, extra) {
    if (this.props.debug === true) {
      // eslint-disable-next-line no-console
      console.log(`[Websocket] ${event}`, extra || '')
    }
  }

  generateInterval(k) {
    if (this.props.reconnectIntervalInMillieSeconds > 0) {
      return this.props.reconnectIntervalInMillieSeconds
    }
    // Exponential backoff capped at 30s.
    return Math.min(30, Math.pow(2, k) - 1) * 1000
  }

  openWebsocket() {
    // Item 2: never open a second connection while one is already OPEN or
    // CONNECTING -- that is the duplicate-connection source behind the server
    // fd exhaustion.
    if (
      this.ws &&
      (this.ws.readyState === WS_OPEN || this.ws.readyState === WS_CONNECTING)
    ) {
      this.diag('DUPLICATE_CONNECT_BLOCKED', { readyState: this.ws.readyState })
      return
    }

    // Tear down any previous (closed/closing) socket before creating a new one.
    this.closeWebsocket()

    const WS = window.WebSocket || window.MozWebSocket
    const socket = new WS(this.props.url, this.props.protocol)
    this.ws = socket
    this.diag('CONNECTING')
    this.bindHandlers(socket)
  }

  // Item 1: bind handlers directly to the socket being created (closure over
  // `socket`), so a callback firing later always refers to the correct socket,
  // never whatever is "current".
  bindHandlers(socket) {
    socket.onopen = () => {
      // Item 4: ignore events from a socket that is no longer the active one.
      if (socket !== this.ws) {
        this.diag('STALE_OPEN')
        return
      }
      this.diag('OPEN')
      // Reset backoff only AFTER the socket has stayed open long enough to be
      // healthy. A connection that opens then closes immediately (flapping)
      // keeps its elevated `attempts`, so reconnects back off instead of
      // hammering every ~1000ms. A genuinely stable connection resets normally.
      if (this.stableTimerID) {
        clearTimeout(this.stableTimerID)
      }
      this.stableTimerID = setTimeout(() => {
        this.stableTimerID = null
        if (socket === this.ws) {
          this.diag('STABLE_RESET')
          this.attempts = 1
        }
      }, this.stableConnectionThresholdMs)
      if (typeof this.props.onOpen === 'function') this.props.onOpen()
    }

    socket.onerror = (e) => {
      if (socket !== this.ws) return
      if (typeof this.props.onError === 'function') this.props.onError(e)
    }

    socket.onmessage = (evt) => {
      if (socket !== this.ws) return
      if (typeof this.props.onMessage === 'function') {
        this.props.onMessage(evt.data)
      }
    }

    socket.onclose = (evt) => {
      // Item 4: a stale socket (already replaced) firing onclose must not
      // schedule another reconnect.
      if (socket !== this.ws) {
        this.diag('STALE_CLOSE', { code: evt.code })
        return
      }
      this.diag('CLOSE', { code: evt.code, reason: evt.reason })
      // Socket closed before it was confirmed stable: cancel the pending
      // backoff reset so a flapping connection keeps backing off.
      if (this.stableTimerID) {
        clearTimeout(this.stableTimerID)
        this.stableTimerID = null
      }
      if (typeof this.props.onClose === 'function') {
        this.props.onClose(evt.code, evt.reason)
      }
      this.scheduleReconnect()
    }
  }

  scheduleReconnect() {
    if (!this.shouldReconnect) return
    // Item 3: only one reconnect timer may exist at a time.
    if (this.timeoutID) {
      this.diag('DUPLICATE_RECONNECT_BLOCKED')
      return
    }
    const time = this.generateInterval(this.attempts)
    this.diag('RECONNECT_SCHEDULED', { attempt: this.attempts, inMs: time })
    this.timeoutID = setTimeout(() => {
      this.timeoutID = null
      this.attempts += 1
      this.diag('RECONNECT_START', { attempt: this.attempts })
      this.openWebsocket()
    }, time)
  }

  closeWebsocket() {
    const socket = this.ws
    if (!socket) return
    // Detach handlers before closing so the discarded socket's late onclose
    // cannot trigger another reconnect.
    socket.onopen = null
    socket.onmessage = null
    socket.onerror = null
    socket.onclose = null
    try {
      socket.close()
    } catch (e) {
      // ignore: socket may already be closing/closed
    }
    this.ws = null
  }

  componentDidMount() {
    this.diag('MOUNT')
    this.shouldReconnect = this.props.reconnect
    this.openWebsocket()
  }

  // Item 5: on unmount disable reconnect, clear any pending timer, detach
  // handlers and close the active socket.
  componentWillUnmount() {
    this.diag('UNMOUNT')
    this.shouldReconnect = false
    if (this.timeoutID) {
      clearTimeout(this.timeoutID)
      this.timeoutID = null
    }
    if (this.stableTimerID) {
      clearTimeout(this.stableTimerID)
      this.stableTimerID = null
    }
    this.closeWebsocket()
  }

  // Item 7: only send when the socket is actually OPEN.
  sendMessage(message) {
    if (this.ws && this.ws.readyState === WS_OPEN) {
      this.ws.send(message)
      return true
    }
    this.diag('SEND_SKIPPED_NOT_OPEN', {
      readyState: this.ws ? this.ws.readyState : 'none',
    })
    return false
  }

  render() {
    return <div></div>
  }
}

Websocket.defaultProps = {
  debug: false,
  reconnect: false,
}
